import {RefObject, useEffect, useRef} from 'react';

/** 스크롤 진행률 수집 단계 (%) */
export const SCROLL_DEPTHS = [25, 50, 75, 100] as const;

export type ScrollDepthType = (typeof SCROLL_DEPTHS)[number];

interface IOptions {
  /** 진행률을 계산할 콘텐츠 엘리먼트 */
  contentRef: RefObject<HTMLElement | null>;
  /** 콘텐츠를 잘라내는 스크롤 컨테이너. enabled/resetKey가 바뀔 때 다시 조회한다 */
  getScrollContainer(): HTMLElement | null;
  /** 콘텐츠가 실제로 화면에 보이는 동안에만 true로 둔다 (false면 측정 중단) */
  enabled?: boolean;
  /** 값이 바뀌면 진행률을 0%부터 다시 측정한다 */
  resetKey?: string;
  onReach(depth: ScrollDepthType): void;
}

/**
 * 콘텐츠를 어디까지 읽었는지 단계별(25/50/75/100%)로 1회씩 알려준다.
 * 진행률은 스크롤 컨테이너의 보이는 영역 하단이 콘텐츠의 몇 %에 닿았는지로 계산하므로,
 * 스크롤 없이 전체가 보이는 짧은 콘텐츠는 즉시 100%로 처리된다.
 */
export const useScrollDepth = ({
  contentRef,
  getScrollContainer,
  enabled,
  resetKey,
  onReach,
}: IOptions) => {
  const reachedRef = useRef<number[]>([]);
  const getContainerRef = useRef(getScrollContainer);
  const onReachRef = useRef(onReach);

  // 렌더 도중 ref를 쓰지 않도록 커밋 이후에 최신 콜백으로 갱신한다
  useEffect(() => {
    getContainerRef.current = getScrollContainer;
    onReachRef.current = onReach;
  });

  useEffect(() => {
    reachedRef.current = [];
  }, [resetKey]);

  useEffect(() => {
    const container = getContainerRef.current();
    const content = contentRef.current;

    if (!enabled || !container || !content) {
      return;
    }

    const measure = () => {
      if (reachedRef.current.length === SCROLL_DEPTHS.length) {
        return;
      }

      const {top, height} = content.getBoundingClientRect();

      if (height <= 0) {
        return;
      }

      const ratio = (container.getBoundingClientRect().bottom - top) / height;
      const percent = Math.min(Math.max(ratio, 0), 1) * 100;

      SCROLL_DEPTHS.forEach((depth) => {
        if (percent >= depth && !reachedRef.current.includes(depth)) {
          reachedRef.current.push(depth);
          onReachRef.current(depth);
        }
      });
    };

    measure();

    // 콘텐츠 높이(마크다운 렌더 지연)와 컨테이너 높이(탭 줄바꿈, 뷰포트 변화) 모두 추적
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    observer.observe(container);
    container.addEventListener('scroll', measure, {passive: true});
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      container.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, resetKey]);
};
